import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  Call,
  CallEnd,
  Refresh,
  Sync,
  SyncDisabled,
  Sensors,
} from '@mui/icons-material';

import {EnhancedConnectionState} from '../types/EnhancedConnectionState';
import {TourDialog} from '../../components/TourDialog';
import {TourPreviewLeadCapture} from './TourPreviewLeadCapture';
import {useTruvuDialog} from '../../../../components/dialog/TruvuDialog2';

const ControlsContainer = styled(Box)(({theme}) => ({
  alignSelf: 'stretch',
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0.5rem',
  zIndex: 50,
  position: 'absolute',
  bottom: theme.spacing(2),
  left: theme.spacing(2),
  right: theme.spacing(2),
}));

interface CallControlsProps {
  isHost: boolean;
  isMicActive: boolean;
  isVideoActive: boolean;
  isSyncActive: boolean;
  connectionState: EnhancedConnectionState;
  connectionError: Error | null;
  reconnectAttempts: number;
  toggleMic: () => void;
  toggleVideo: () => void;
  toggleSync: () => void;
  leaveCall: () => void;
  resetConnection?: () => void;
}

export function CallControls({
  isMicActive,
  isVideoActive,
  isSyncActive,
  connectionState,
  connectionError,
  toggleMic,
  toggleVideo,
  toggleSync,
  leaveCall,
  resetConnection,
}: CallControlsProps) {
  const isConnecting =
    connectionState === 'CONNECTING' || connectionState === 'RECONNECTING';
  const hasError = connectionState === 'ERROR' || !!connectionError;

  return (
    <ControlsContainer>
      <Tooltip title={isVideoActive ? 'Turn camera off' : 'Turn camera on'}>
        <Button
          variant="contained"
          color={isVideoActive ? 'primary' : 'secondary'}
          onClick={toggleVideo}
          disabled={isConnecting || hasError}
        >
          {isVideoActive ? <Videocam /> : <VideocamOff />}
        </Button>
      </Tooltip>
      <Tooltip title={isSyncActive ? 'Turn camera off' : 'Turn camera on'}>
        <Button
          variant="contained"
          color={isSyncActive ? 'primary' : 'secondary'}
          onClick={toggleSync}
          disabled={isConnecting || hasError}
        >
          {isSyncActive ? <Sync /> : <SyncDisabled />}
        </Button>
      </Tooltip>

      <Tooltip title={hasError ? 'Retry connection' : 'End call'}>
        <Button
          variant="contained"
          color="error"
          onClick={hasError && resetConnection ? resetConnection : leaveCall}
        >
          {hasError ? <Refresh /> : <CallEnd sx={{color: 'white'}} />}
        </Button>
      </Tooltip>

      <Tooltip title={isMicActive ? 'Mute microphone' : 'Unmute microphone'}>
        <Button
          variant="contained"
          color={isMicActive ? 'primary' : 'secondary'}
          onClick={toggleMic}
          disabled={isConnecting || hasError}
        >
          {isMicActive ? <Mic /> : <MicOff />}
        </Button>
      </Tooltip>
    </ControlsContainer>
  );
}

interface CallButtonProps {
  isHost: boolean;
  tourId: string;
  onClick: () => void;
  isStartingCall?: boolean;
  connectionState?: EnhancedConnectionState;
}

export function CallButton({
  onClick,
  tourId,
  isHost,
  isStartingCall = false,
  connectionState = 'DISCONNECTED',
}: CallButtonProps) {
  const dialogProps = useTruvuDialog();
  const isConnecting =
    connectionState === 'CONNECTING' || connectionState === 'RECONNECTING';
  const isDisabled = isStartingCall || isConnecting;
  const defaultText = isHost ? 'Go Live' : 'Talk to Agent';
  const buttonText = isStartingCall
    ? 'Initializing...'
    : isConnecting
    ? 'Connecting...'
    : defaultText;

  const handleClick = () => {
    const storedDetails = localStorage.getItem('capturedDetails');
    if (storedDetails != null || isHost) {
      onClick();
      return;
    }
    dialogProps.onOpen();
  };

  return (
    <>
      <TourDialog title="Send notification for agent to join" {...dialogProps}>
        <TourPreviewLeadCapture
          tourId={tourId}
          onCancel={dialogProps.onClose}
          onComplete={onClick}
        />
      </TourDialog>
      <Button
        sx={{
          mb: 2,
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
        }}
        variant="contained"
        color="primary"
        startIcon={
          isDisabled ? (
            <CircularProgress size={20} color="inherit" />
          ) : isHost ? (
            <Sensors />
          ) : (
            <Call />
          )
        }
        onClick={handleClick}
        disabled={isDisabled}
      >
        {buttonText}
      </Button>
    </>
  );
}

export const ConnectionErrorDisplay: React.FC<{
  connectionError: Error | null;
  connectionState: EnhancedConnectionState;
  reconnectAttempts: number;
  resetConnection?: () => void;
}> = ({connectionError, connectionState, resetConnection}) => {
  if (!connectionError && connectionState !== 'ERROR') {
    return null;
  }

  const errorMessage = connectionError?.message || 'Connection error occurred';

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '7rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 51,
        backgroundColor: '#f44336',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {errorMessage}
      {resetConnection && (
        <IconButton size="small" color="inherit" onClick={resetConnection}>
          <Refresh fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
